import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { API_URL } from '../config/constants';
import { buildAuthHeaders, clearStoredAuth, getStoredAuthToken, getTokenPayload, isTokenExpired } from '../utils/auth';

// Definim interfața pentru utilizator
interface User {
  id: number;
  name: string;
  email: string;
  role: 'user' | 'admin';
}

// Interfața pentru contextul de autentificare
interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  clearError: () => void;
}

// Valoarea inițială pentru context
const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  isLoading: true,
  error: null,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  isAuthenticated: false,
  isAdmin: false,
  clearError: () => {},
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Verifică și reînnoiește token-ul dacă este aproape de expirare
  const checkAndRefreshToken = async () => {
    if (!token) return;

    try {
      if (isTokenExpired(token)) {
        logout();
        return;
      }

      const tokenData = getTokenPayload(token);
      if (!tokenData?.exp) {
        logout();
        return;
      }

      const expirationTime = tokenData.exp * 1000; // Convertim în milisecunde
      const currentTime = Date.now();
      const timeUntilExpiration = expirationTime - currentTime;

      // Dacă token-ul expiră în mai puțin de 5 minute, îl reînnoim
      if (timeUntilExpiration < 300000) { // 5 minute în milisecunde
        const response = await fetch(`${API_URL}/auth/refresh-token`, {
          method: 'POST',
          headers: buildAuthHeaders(token)
        });

        if (response.ok) {
          const data = await response.json();
          const newToken = data.token;
          setToken(newToken);
          localStorage.setItem('authToken', newToken);
        } else {
          logout();
        }
      }
    } catch (error) {
      console.error('Eroare la verificarea token-ului:', error);
      logout();
    }
  };

  // Verifică token-ul la fiecare 4 minute
  useEffect(() => {
    if (token) {
      const interval = setInterval(checkAndRefreshToken, 240000); // 4 minute
      return () => clearInterval(interval);
    }
  }, [token]);

  // Verificăm dacă există un token stocat la încărcarea aplicației
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = getStoredAuthToken();
      const storedUser = localStorage.getItem('user');
      
      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
        } catch (error) {
          clearStoredAuth();
        }
      } else {
        clearStoredAuth();
      }
      
      setIsLoading(false);
    };
    
    initAuth();
  }, []);

  // Resetăm eroarea
  const clearError = () => {
    setError(null);
  };

  // Funcția de login
  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      clearError();
      
      console.log('Încercare de autentificare pentru:', email);
      
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password }),
      });
      
      console.log('Răspuns primit:', response.status, response.statusText);
      
      if (!response.ok) {
        if (response.status === 0 || response.status === 403) {
          console.error('Eroare CORS: Serverul nu permite cererea');
          throw new Error('Eroare de conexiune cu serverul. Verificați consola pentru detalii.');
        }
        
        const errorData = await response.json().catch(() => {
          throw new Error(`Eroare server: ${response.status} ${response.statusText}`);
        });
        
        throw new Error(errorData.message || 'Eroare la autentificare');
      }
      
      const data = await response.json();
      console.log('Conținut răspuns:', data);
      
      // Compatibilitate cu răspunsul API-ului: name ar putea fi stocat ca username
      const userData: User = {
        id: data.user.id,
        name: data.user.name || data.user.username, // Folosim name dacă există, altfel username
        email: data.user.email,
        role: data.user.role,
      };
      
      console.log('Autentificare reușită:', userData);
      
      // Salvăm datele în localStorage și state
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      setToken(data.token);
      setUser(userData);
    } catch (error: any) {
      console.error('Eroare la procesarea autentificării:', error);
      setError(error.message || 'Eroare necunoscută la autentificare');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Funcția de înregistrare
  const register = async (name: string, email: string, password: string) => {
    try {
      setIsLoading(true);
      clearError();
      
      console.log('Încercare de înregistrare pentru:', email);
      
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          name,  // Backend-ul în authController convertește name la username
          email, 
          password,
          role: 'user' // Rolul implicit este 'user'
        }),
      });
      
      console.log('Răspuns înregistrare primit:', response.status, response.statusText);
      
      if (!response.ok) {
        if (response.status === 0 || response.status === 403) {
          console.error('Eroare CORS: Serverul nu permite cererea');
          throw new Error('Eroare de conexiune cu serverul. Verificați consola pentru detalii.');
        }
        
        const errorData = await response.json().catch(() => {
          throw new Error(`Eroare server: ${response.status} ${response.statusText}`);
        });
        
        throw new Error(errorData.message || 'Eroare la înregistrare');
      }
      
      const data = await response.json();
      console.log('Conținut răspuns înregistrare:', data);
      
      console.log('Înregistrare reușită');
      
      // Nu autentificăm automat utilizatorul după înregistrare
      // Redirecționăm către pagina de login
    } catch (error: any) {
      console.error('Eroare la procesarea înregistrării:', error);
      setError(error.message || 'Eroare necunoscută la înregistrare');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Funcția de logout
  const logout = () => {
    setToken(null);
    setUser(null);
    clearStoredAuth();
  };

  // Valori computate pentru a verifica ușor starea autentificării
  const isAuthenticated = !!token && !!user && !isTokenExpired(token);
  const isAdmin = isAuthenticated && user?.role === 'admin';

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        error,
        login,
        register,
        logout,
        isAuthenticated,
        isAdmin,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Hook personalizat pentru a utiliza contextul mai ușor
export const useAuth = () => useContext(AuthContext);

export default AuthContext; 
